import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const RegistrationPage = () => {
  const { user, completeRegistration } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const [role, setRole] = useState('student');
  const [studentType, setStudentType] = useState('high_school_11_12');
  const [examType, setExamType] = useState('both');
  const [level, setLevel] = useState('intermediate');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (user.role !== 'student') {
      // User already registered
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const registrationData = {
        role,
        ...(role === 'student' && {
          student_type: studentType,
          exam_type: examType,
          level: level
        })
      };

      await completeRegistration(registrationData);
      navigate('/dashboard');
    } catch (err) {
      setError(language === 'tr' ? 'Kayıt tamamlanamadı. Lütfen tekrar deneyin.' : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <LanguageToggle />
      
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎓</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            {language === 'tr' ? 'Profilinizi Tamamlayın' : 'Complete Your Profile'}
          </h1>
          <p className="text-gray-600">
            {language === 'tr' ? 'Size en uygun deneyimi sunabilmemiz için birkaç bilgiye ihtiyacımız var.' : 'We need a few details to provide you with the best experience.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                {language === 'tr' ? 'Rolünüzü seçin' : 'Select your role'}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setRole('student')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    role === 'student'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="role-student"
                >
                  <div className="text-4xl mb-2">🎓</div>
                  <div className="font-semibold">{language === 'tr' ? 'Öğrenci' : 'Student'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('parent')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    role === 'parent'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="role-parent"
                >
                  <div className="text-4xl mb-2">👨‍👩‍👧</div>
                  <div className="font-semibold">{language === 'tr' ? 'Veli' : 'Parent'}</div>
                </button>
              </div>
            </div>

            {/* Student-specific fields */}
            {role === 'student' && (
              <>
                {/* Student Type */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-3">
                    {language === 'tr' ? 'Öğrenci Tipi' : 'Student Type'}
                  </label>
                  <select
                    value={studentType}
                    onChange={(e) => setStudentType(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none"
                    data-testid="student-type-select"
                  >
                    <option value="high_school_9_10">{language === 'tr' ? 'Lise 9-10' : 'High School 9-10'}</option>
                    <option value="high_school_11_12">{language === 'tr' ? 'Lise 11-12' : 'High School 11-12'}</option>
                    <option value="graduate">{language === 'tr' ? 'Mezun' : 'Graduate'}</option>
                    <option value="adult">{language === 'tr' ? 'Yetişkin' : 'Adult'}</option>
                  </select>
                </div>

                {/* Exam Type */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-3">
                    {language === 'tr' ? 'Sınav Tipi' : 'Exam Type'}
                  </label>
                  <select
                    value={examType}
                    onChange={(e) => setExamType(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none"
                    data-testid="exam-type-select"
                  >
                    <option value="tyt">{language === 'tr' ? 'TYT Hazırlanan' : 'TYT Preparation'}</option>
                    <option value="ayt">{language === 'tr' ? 'AYT Hazırlanan' : 'AYT Preparation'}</option>
                    <option value="ap">{language === 'tr' ? 'AP Hazırlanan' : 'AP Preparation'}</option>
                    <option value="both">{language === 'tr' ? 'Her İkisi' : 'Both AP + TYT/AYT'}</option>
                  </select>
                </div>

                {/* Level */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-3">
                    {language === 'tr' ? 'Seviye' : 'Level'}
                  </label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none"
                    data-testid="level-select"
                  >
                    <option value="beginner">{language === 'tr' ? 'Başlangıç' : 'Beginner'}</option>
                    <option value="intermediate">{language === 'tr' ? 'Orta' : 'Intermediate'}</option>
                    <option value="advanced">{language === 'tr' ? 'İleri' : 'Advanced'}</option>
                    <option value="expert">{language === 'tr' ? 'Uzman' : 'Expert'}</option>
                  </select>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold text-lg"
              data-testid="complete-registration-button"
            >
              {loading
                ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...')
                : (language === 'tr' ? 'Kaydı Tamamla' : 'Complete Registration')
              }
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;